import React from 'react';
import { Link } from 'react-router-dom';
import { Package, ArrowLeft } from 'lucide-react';
import { Button } from './ui/button';

const Section = ({ title, children }) => (
  <div className="mb-8">
    <h2 className="text-lg font-semibold text-primary mb-3">{title}</h2>
    <div className="text-sm text-muted-foreground leading-relaxed space-y-3">{children}</div>
  </div>
);

const TermsConditions = () => (
  <div className="min-h-screen bg-background font-manrope">
    <header className="border-b bg-white/80 backdrop-blur-md sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <Package className="w-8 h-8 text-accent" />
          <span className="text-xl font-bold text-primary">KAHA</span>
        </Link>
      </div>
    </header>

    <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <Button variant="ghost" asChild className="mb-6 gap-2" data-testid="btn-back-home">
        <Link to="/"><ArrowLeft className="w-4 h-4" /> Volver al inicio</Link>
      </Button>

      <h1 className="text-2xl sm:text-3xl font-bold text-primary mb-2">Términos y Condiciones</h1>
      <p className="text-sm text-muted-foreground mb-10">Última actualización: marzo 2026</p>

      <Section title="1. Descripción del servicio">
        <p>KAHA Packaging (en adelante "KAHA") es una empresa mexicana dedicada a la fabricación y venta de cajas de cartón corrugado con impresión personalizada. A través de nuestra plataforma web, ofrecemos un sistema de cotización instantánea, configuración de medidas, selección de materiales e impresión digital.</p>
        <p>Al realizar un pedido a través de nuestro sitio, el cliente acepta los presentes términos y condiciones en su totalidad.</p>
      </Section>

      <Section title="2. Proceso de pedido">
        <p>El proceso consta de los siguientes pasos:</p>
        <ol className="list-decimal list-inside space-y-1 pl-2">
          <li>Configuración de la caja: dimensiones, material, cantidad y opciones de impresión.</li>
          <li>Revisión de la cotización generada automáticamente.</li>
          <li>Llenado de datos de facturación y envío.</li>
          <li>Selección del método de envío o recolección en planta.</li>
          <li>Pago a través de los métodos disponibles.</li>
          <li>Confirmación del pedido y envío de comprobante por correo electrónico.</li>
        </ol>
        <p>Una vez confirmado el pago, el pedido ingresa a producción y no podrá ser modificado.</p>
      </Section>

      <Section title="3. Tiempos de producción y entrega">
        <p>El tiempo estimado de producción es de <strong>5 días hábiles</strong> a partir de la confirmación del pago y la aprobación del arte.</p>
        <p>El tiempo de entrega depende del método de envío seleccionado y la ubicación del destino. KAHA no se hace responsable por retrasos causados por la paquetería una vez que el producto ha sido entregado al servicio de mensajería.</p>
        <p>Para pedidos con recolección en planta, el cliente será notificado cuando su pedido esté listo para ser recogido.</p>
      </Section>

      <Section title="4. Responsabilidad del cliente sobre diseños">
        <p>El cliente es responsable de proporcionar archivos de diseño en las especificaciones indicadas. KAHA no se hace responsable por errores en el diseño proporcionado por el cliente, incluyendo pero no limitado a:</p>
        <ul className="list-disc list-inside space-y-1 pl-2">
          <li>Errores ortográficos o de contenido.</li>
          <li>Resolución insuficiente de las imágenes.</li>
          <li>Colores fuera del espacio CMYK.</li>
          <li>Elementos fuera del área de corte o sangrado.</li>
        </ul>
      </Section>

      <Section title="5. Aprobación de archivos">
        <p>Antes de iniciar la producción, KAHA revisará los archivos proporcionados. Si se detectan problemas técnicos, se notificará al cliente para su corrección. KAHA se reserva el derecho de no iniciar la producción hasta que se reciban archivos que cumplan con los requisitos técnicos.</p>
        <p>La aprobación final del arte es responsabilidad del cliente.</p>
      </Section>

      <Section title="6. Políticas de pago">
        <p>El pago total del pedido debe realizarse al momento de la compra. Aceptamos pagos a través de:</p>
        <ul className="list-disc list-inside space-y-1 pl-2">
          <li>Mercado Pago (tarjeta de crédito, débito, transferencia, OXXO).</li>
        </ul>
        <p>Los precios mostrados en el sitio incluyen IVA. La facturación es opcional y puede solicitarse después de realizado el pago.</p>
      </Section>

      <Section title="7. Políticas de cancelación y devoluciones">
        <p><strong>Cancelaciones:</strong> Los pedidos pueden cancelarse únicamente antes de que ingresen a producción. Una vez iniciada la producción, no se aceptan cancelaciones.</p>
        <p><strong>Devoluciones:</strong> Dado que cada caja es fabricada a medida según las especificaciones del cliente, no se aceptan devoluciones salvo que el producto presente defectos de fabricación atribuibles a KAHA. En ese caso, el cliente deberá notificar dentro de las 48 horas posteriores a la recepción del pedido.</p>
        <p>KAHA evaluará cada caso y, de proceder, realizará la reposición del producto sin costo adicional.</p>
      </Section>

      <Section title="8. Tolerancias de producción">
        <p>Debido a la naturaleza del proceso de manufactura en cartón corrugado, pueden existir variaciones dentro de los siguientes rangos aceptables:</p>
        <ul className="list-disc list-inside space-y-1 pl-2">
          <li>Dimensiones: ±3 mm.</li>
          <li>Color de impresión: variaciones leves respecto al diseño digital (las pantallas no reproducen colores CMYK con exactitud).</li>
          <li>Registro de impresión: ±2 mm.</li>
        </ul>
        <p>Estas tolerancias son estándar en la industria y no constituyen defectos de fabricación.</p>
      </Section>

      <Section title="9. Limitación de responsabilidad">
        <p>KAHA no será responsable por daños indirectos, incidentales o consecuentes derivados del uso de nuestros productos o servicios. Nuestra responsabilidad máxima se limita al monto pagado por el pedido en cuestión.</p>
        <p>KAHA se reserva el derecho de modificar estos términos en cualquier momento. Las modificaciones serán publicadas en esta página y entrarán en vigor a partir de su publicación.</p>
      </Section>

      <Section title="10. Contacto">
        <p>Para cualquier duda sobre estos términos, puede contactarnos en:</p>
        <ul className="list-disc list-inside space-y-1 pl-2">
          <li>Correo electrónico: <a href="mailto:hola@kahamx.com" className="text-accent hover:underline">hola@kahamx.com</a></li>
          <li>WhatsApp: <a href="https://wa.me/524778055212" className="text-accent hover:underline">+52 477 805 5212</a></li>
        </ul>
      </Section>
    </main>

    <footer className="py-8 bg-white border-t">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <p className="text-sm text-muted-foreground">© {new Date().getFullYear()} KAHA Packaging. Todos los derechos reservados.</p>
      </div>
    </footer>
  </div>
);

export default TermsConditions;
